import React, { ReactNode } from 'react';
import Link from 'next/link';
import { formatLarge } from '@benzinga/utils';
import { useBenzingaEdge } from '@benzinga/edge';

export const TickerCell: React.FC<{
  ticker: string;
  name?: string;
}> = ({ ticker, name }) => {
  const { hasEdgeSubscription: hasBzEdge } = useBenzingaEdge();
  return (
    <div>
      {hasBzEdge ? (
        <Link
          href={`/quote/${ticker}`}
          className="font-semibold text-base text-black hover:text-blue-500"
          prefetch={false}
        >
          {ticker}
        </Link>
      ) : (
        <span className="font-semibold text-base text-black opacity-70">{ticker}</span>
      )}

      {name && <div className="text-[11px] text-[#929EAE] mt-0.5">{name}</div>}
    </div>
  );
};

export const PriceChangeBadge: React.FC<{
  change: number;
  changePercent?: number;
  showPercent?: boolean;
}> = ({ change, changePercent, showPercent = true }) => {
  const isPositive = change >= 0;
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-1 rounded-md font-semibold text-sm"
      style={{
        color: isPositive ? '#2FC05F' : '#ff3f51',
        backgroundColor: isPositive ? '#eaf9ef' : '#ffeced',
      }}
    >
      <span className="text-xs">{isPositive ? '▲' : '▼'}</span>
      <span>
        {showPercent && changePercent !== undefined
          ? `${Math.abs(change).toFixed(2)} (${Math.abs(changePercent).toFixed(2)}%)`
          : `${Math.abs(change).toFixed(2)}%`}
      </span>
    </span>
  );
};

export const VolumeCell: React.FC<{ volume: number | undefined }> = ({ volume }) => (
  <div className="text-sm font-semibold text-gray-700">{volume ? formatLarge(volume) : '--'}</div>
);

export const SimpleTextCell: React.FC<{
  children: ReactNode;
  bold?: boolean;
  size?: 'xs' | 'sm' | 'base';
  color?: string;
  title?: string;
}> = ({ children, bold = false, size = 'sm', color = 'text-gray-700' }) => {
  const sizeClasses = { xs: 'text-xs', sm: 'text-sm', base: 'text-base' };
  return <div className={`${sizeClasses[size]} ${bold ? 'font-semibold' : ''} ${color}`}>{children || '--'}</div>;
};

export const TextCell: React.FC<{ children: ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`text-black ${className}`}>{children}</div>
);

const formatTime = (time: string | undefined) => {
  if (!time) return '--';
  try {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch {
    return time;
  }
};

export const TimeCell: React.FC<{ time?: string }> = ({ time }) => {
  return <div className="text-gray-500">{formatTime(time)}</div>;
};

export const EstimateCell: React.FC<{
  value?: string | number;
  showArrow?: boolean;
}> = ({ value, showArrow = true }) => {
  if (!value) {
    return <span className="text-gray-400">--</span>;
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  const isPositive = numValue >= 0;

  return (
    <div className="flex items-center">
      <span
        className="inline-flex items-center justify-between px-4 rounded-md font-semibold min-w-[100px]"
        style={{
          color: isPositive ? '#2FC05F' : '#ff3f51',
          backgroundColor: isPositive ? '#eaf9ef' : '#ffeced',
        }}
      >
        {showArrow && <span className="text-xs">{isPositive ? '▲' : '▼'}</span>}
        <span>{formatLarge(numValue)}</span>
      </span>
    </div>
  );
};
